import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMedia } from 'react-use';
import { Button, Checkbox, Drawer, IconButton, Spinner, useSnackbar } from '@c2fo/liquidity';
import { ArrowLeftIcon, SyncIcon } from '@c2fo/liquidity/icons';
import { useRefetchStatsQueries } from '@/lib/serverSentEvents/useServerSideEventListeners';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { clearMarket, getActiveOffersWithEligibleInvoices } from '../demoSerialize';

type ClearableMarket = ReturnType<typeof getActiveOffersWithEligibleInvoices>[number];

const DEMO_CLEARABLE_MARKETS_KEY = ['demo-clearable-markets'] as const;

const SimulateClearContent = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { asCurrency } = useLocaleFormat();
  const queryClient = useQueryClient();
  const [selectedOffers, setSelectedOffers] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const { refetchManyTakerMarkets } = useRefetchStatsQueries();

  // Read directly from @msw/data collections. The Drawer keeps children mounted while closed,
  // so `enabled: isOpen` + `refetchOnMount: 'always'` forces a fresh collection read every
  // time the drawer opens — picks up any offers submitted since the last open.
  const { data: clearableMarkets = [] } = useQuery<ClearableMarket[]>({
    queryKey: DEMO_CLEARABLE_MARKETS_KEY,
    queryFn: () => getActiveOffersWithEligibleInvoices(),
    enabled: isOpen,
    refetchOnMount: 'always',
    staleTime: 0,
  });

  const handleToggleOffer = (offerUuid: string) => {
    setSelectedOffers((prev) => {
      const next = new Set(prev);
      if (next.has(offerUuid)) {
        next.delete(offerUuid);
      } else {
        next.add(offerUuid);
      }

      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedOffers.size === clearableMarkets.length) {
      setSelectedOffers(new Set());
    } else {
      setSelectedOffers(new Set(clearableMarkets.map((m) => m.offerUuid)));
    }
  };

  const handleSimulateClear = async () => {
    if (selectedOffers.size === 0) return;

    setIsProcessing(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));

      const selected = clearableMarkets.filter((m) => selectedOffers.has(m.offerUuid));

      for (const market of selected) {
        await clearMarket(market.offerUuid);
      }

      // Invalidate the demo store query so the list refreshes on next open.
      await queryClient.invalidateQueries({ queryKey: DEMO_CLEARABLE_MARKETS_KEY });

      // Invalidate history/awards queries so the Recent Awards section and History page
      // pick up the newly cleared offers without requiring a page reload.
      //
      // refetchType: 'all' is required here. The clear is simulated from the dashboard drawer while
      // the history pages are unmounted, so their queries are inactive. The default refetchType
      // ('active') would only mark inactive queries stale without refetching them; combined with the
      // global `refetchOnMount: false` (see lib/queryClient.ts), navigating back to a history page
      // then serves the stale cached result (missing the new clear). Forcing 'all' refetches the
      // inactive cached queries now so the cache is fresh on the next mount.
      await queryClient.invalidateQueries({ queryKey: ['recent-awards'], refetchType: 'all' });
      await queryClient.invalidateQueries({ queryKey: ['taker-history'], refetchType: 'all' });
      await queryClient.invalidateQueries({ queryKey: ['taker-history-stats'], refetchType: 'all' });
      await queryClient.invalidateQueries({ queryKey: ['early-pay-history-widget'], refetchType: 'all' });
      await queryClient.invalidateQueries({ queryKey: ['early-pay-history-widget-all-time'], refetchType: 'all' });
      await queryClient.invalidateQueries({ queryKey: ['clear-history'], refetchType: 'all' });

      // Re-fetch taker-markets so the dashboard reads the mutated @msw/data collections —
      // clearMarket() stamped cleared=true, so getSupplierMarkets/getOffers now return
      // post-clear stats (eligible→0, awarded/clearing populated). Without this the UI cache
      // stays frozen on the pre-clear snapshot.
      await refetchManyTakerMarkets(
        selected.map((m) => ({ marketUuid: m.marketUuid, takerUuid: m.supplierDivisionUuid }))
      );

      snackbar({
        message: t('demo.simulateClear.successMessage', { count: selectedOffers.size }),
      });

      setSelectedOffers(new Set());
      onClose();
    } catch {
      snackbar({ message: t('demo.simulateClear.errorMessage') });
    } finally {
      setIsProcessing(false);
    }
  };

  if (clearableMarkets.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-4">
        <p className="text-center text-sm text-text-secondary">{t('demo.simulateClear.noEligibleMarkets')}</p>
      </div>
    );
  }

  const allSelected = selectedOffers.size === clearableMarkets.length && clearableMarkets.length > 0;

  return (
    <div className="flex h-full flex-col">
      <div className="border-b p-4">
        <p className="text-sm text-text-secondary">{t('demo.simulateClear.description')}</p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {clearableMarkets.length > 1 && (
          <div className="border-b p-4">
            <Checkbox
              checked={allSelected}
              indeterminate={selectedOffers.size > 0 && selectedOffers.size < clearableMarkets.length}
              label={
                <span className="font-semibold">
                  {t('demo.simulateClear.selectAll')} ({clearableMarkets.length})
                </span>
              }
              onChange={handleSelectAll}
            />
          </div>
        )}

        <div className="divide-y">
          {clearableMarkets.map((market) => (
            <div key={market.offerUuid} className="p-4 hover:bg-gray-50">
              <Checkbox
                checked={selectedOffers.has(market.offerUuid)}
                label={
                  <div className="flex flex-col gap-1">
                    <div className="font-medium text-text-primary">{market.makerOrganizationName}</div>
                    <div className="text-sm text-text-secondary">{market.marketDisplayName}</div>
                    <div className="text-xs text-text-secondary">
                      {t('demo.simulateClear.eligibleInvoices', {
                        included: market.includedInvoiceCount,
                        total: market.totalInvoiceCount,
                        amount: asCurrency(market.includedInvoiceAmount, market.currency),
                      })}
                    </div>
                  </div>
                }
                onChange={() => handleToggleOffer(market.offerUuid)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="border-t p-4">
        <Button
          className="w-full"
          disabled={selectedOffers.size === 0 || isProcessing}
          onClick={handleSimulateClear}
          variant="primary"
        >
          {isProcessing ? (
            <span className="flex items-center gap-2">
              <Spinner className="size-4" />
              {t('demo.simulateClear.simulating')}
            </span>
          ) : (
            t('demo.simulateClear.simulateButton', { count: selectedOffers.size })
          )}
        </Button>
      </div>
    </div>
  );
};

/**
 * Trigger button + drawer, self-contained. Only render inside demo mode — the caller
 * (DemoBanner) already guards this behind isDemoModeActive().
 */
const SimulateClearDrawer = () => {
  const { t } = useTranslation();
  const isMobile = useMedia('(max-width: 768px)');
  const [isOpen, setIsOpen] = useState(false);

  const closeDrawer = () => setIsOpen(false);
  const openDrawer = () => setIsOpen(true);

  return (
    <>
      {!isMobile ? (
        <Button
          className="[&_span:first-of-type>svg]:size-5 [&_span:first-of-type]:size-5"
          icon={<SyncIcon className="fill-white" />}
          onClick={openDrawer}
          size="sm"
          variant="primary"
        >
          {t('demo.simulateClear.triggerButton')}
        </Button>
      ) : (
        <IconButton
          icon={SyncIcon}
          name={t('demo.simulateClear.triggerButton')}
          onClick={openDrawer}
          variant="primary"
        />
      )}
      <Drawer
        backdropClassName="z-10"
        dialogClassName="z-10 w-full max-w-80 md:max-w-96"
        hideCloseButton
        isOpen={isOpen}
        onClose={closeDrawer}
        title={
          <div className="-ml-1 flex items-center gap-2">
            <IconButton
              className="size-8 [&>svg]:size-5"
              icon={ArrowLeftIcon}
              name={t('core.close')}
              onClick={closeDrawer}
              size="sm"
              variant="ancillary"
            />
            <p className="text-lg font-bold">{t('demo.simulateClear.title')}</p>
          </div>
        }
        titleClassName="border-b"
      >
        <QueryBoundaries>
          <SimulateClearContent isOpen={isOpen} onClose={closeDrawer} />
        </QueryBoundaries>
      </Drawer>
    </>
  );
};

export default SimulateClearDrawer;
